import React, { useState } from 'react';
import { Gift, BookOpen, Heart, Coffee, Star, Users, Clock, Award, Flower2, Apple, Lightbulb, Home } from 'lucide-react';

const ForTeachersPage = ({ setCurrentPage }) => {
  const [selectedOccasion, setSelectedOccasion] = useState('all');

  const teacherOccasions = [
    { id: 'end-of-year', name: 'Skoleårets slutt', icon: Award, color: 'green' },
    { id: 'teachers-day', name: 'Lærernes dag', icon: Star, color: 'blue' },
    { id: 'christmas', name: 'Jul', icon: Heart, color: 'red' },
    { id: 'thanks', name: 'Bare takk', icon: Flower2, color: 'pink' },
    { id: 'retirement', name: 'Pensjonering', icon: Clock, color: 'purple' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-success-50 via-accent-50 to-purple-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">👩‍🏫</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver til lærere og barnehageansatte</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Vis takknemlighet til de som former fremtiden og inspirerer våre barn hver dag. 
              Fra klasseromshjelpere til personlige oppmerksomheter - finn gaver som hedrer deres viktige arbeid.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-purple-600 text-white rounded-lg hover:from-primary-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn lærergaver
            </button>
          </div>
        </div>
      </section>

      {/* Why Teachers Deserve Recognition */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hvorfor fortjener lærere og barnehageansatte anerkjennelse?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <Lightbulb className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">De inspirerer og lærer</h3>
                <p className="text-secondary-600">
                  Lærere former ikke bare akademisk kunnskap, men også karakterutvikling, 
                  kritisk tenkning og livslang læring. De er ofte rollemodeller og mentorer.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <Heart className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">De bryr seg om hver elev</h3>
                <p className="text-secondary-600">
                  Gode lærere ser det unike i hvert barn og jobber for at alle skal lykkes. 
                  De ofrer egen tid og energi for å hjelpe elevene nå sitt potensial.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <Users className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">De støtter familier</h3>
                <p className="text-secondary-600">
                  Lærere er viktige partnere for foreldre og jobber sammen for barnets beste. 
                  De gir trygghet, struktur og støtte i barnets hverdag.
                </p>
              </div>
            </div>

            <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Apple className="w-6 h-6 text-warning-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-warning-800 mb-2">Lærere gjør en forskjell</h3>
                  <p className="text-warning-700">
                    Studier viser at en god lærer kan ha livslang positiv påvirkning på en elevs utvikling. 
                    De huskes ofte som de som trodde på oss, utfordret oss og hjalp oss å vokse. 
                    En liten gest av takknemlighet kan bety enormt mye for dem.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Occasion-Based Gift Ideas */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Gaver til lærere for ulike anledninger
            </h2>

            {/* Occasion Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedOccasion('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedOccasion === 'all'
                    ? 'bg-success-500 text-white border-success-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-success-300'
                }`}
              >
                Alle anledninger
              </button>
              {teacherOccasions.map((occasion) => (
                <button
                  key={occasion.id}
                  onClick={() => setSelectedOccasion(occasion.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedOccasion === occasion.id
                      ? `bg-${occasion.color}-500 text-white border-${occasion.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <occasion.icon className="w-4 h-4" />
                  <span>{occasion.name}</span>
                </button>
              ))}
            </div>

            {/* Occasion Cards */}
            <div className="space-y-8">
              {/* End of Year */}
              <div className={`${selectedOccasion === 'all' || selectedOccasion === 'end-of-year' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Award className="w-10 h-10 text-success-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Skoleårets slutt</h3>
                      <p className="text-secondary-600">Takk for et fantastisk skoleår</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-success-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🏆 Anerkjennelse og minner</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Klassefoto med personlige hilsener</li>
                        <li>• Minnebok med bidrag fra alle elevene</li>
                        <li>• Personaliserte diplomer eller takketegn</li>
                        <li>• Scrapbook med årets høydepunkter</li>
                      </ul>
                    </div>
                    
                    <div className="border border-success-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🌴 Sommerferien nærmer seg</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Avslappende te-kolleksjoner</li>
                        <li>• Bøker for sommerlesen</li>
                        <li>• Spa-produkter for avslapning</li>
                        <li>• Haveredskaper og solkrem</li>
                      </ul>
                    </div>
                    
                    <div className="border border-success-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">📚 Neste skoleår</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Nye kontorrekvisita og organisering</li>
                        <li>• Klasseromsdekorasjoner</li>
                        <li>• Planleggingskalenderen for nytt år</li>
                        <li>• Gave til klassens materialfond</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Teachers Day */}
              <div className={`${selectedOccasion === 'all' || selectedOccasion === 'teachers-day' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Star className="w-10 h-10 text-accent-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Lærernes dag (5. oktober)</h3>
                      <p className="text-secondary-600">Offisiell anerkjennelse av lærerprofesjonen</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="border border-accent-200 rounded-lg p-4">
                        <h4 className="font-semibold text-secondary-800 mb-3">⭐ Profesjonell anerkjennelse</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Stilfulle kontortilbehør</li>
                          <li>• Kvalitets kaffe eller te</li>
                          <li>• Fagbøker eller kurs-gaver</li>
                          <li>• Elegant brevpapir og penn</li>
                        </ul>
                      </div>
                      
                      <div className="border border-accent-200 rounded-lg p-4">
                        <h4 className="font-semibold text-secondary-800 mb-3">🌸 Små oppmerksomheter</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Blomster eller planter</li>
                          <li>• Hjemmelagede kort fra elevene</li>
                          <li>• Sjokolade eller godteri</li>
                          <li>• Personlige takke-beskjeder</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="bg-accent-50 border border-accent-200 rounded-lg p-4">
                      <h4 className="font-semibold text-accent-800 mb-3">💡 Organisering av lærernes dag</h4>
                      <div className="text-sm text-accent-700 space-y-2">
                        <p><strong>Foreldregruppen kan:</strong></p>
                        <ul className="space-y-1 ml-4">
                          <li>• Samle inn penger til felles gavesjer</li>
                          <li>• Organisere at alle barn skriver kort</li>
                          <li>• Arrangere enkel feiring på skolen</li>
                          <li>• Lage takkevideo fra klassen</li>
                        </ul>
                        <p className="mt-3"><strong>Viktig:</strong> Koordiner med andre foreldre så læreren ikke får 25 like gaver!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Christmas */}
              <div className={`${selectedOccasion === 'all' || selectedOccasion === 'christmas' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Heart className="w-10 h-10 text-error-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Julegaver til lærere</h3>
                      <p className="text-secondary-600">Spre julens glede og varme</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="border border-error-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🎄 Juletradisjoner</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Julepynt til klasserommet</li>
                        <li>• Hjemmelaget julekonfekt</li>
                        <li>• Julekaffe og te-blandinger</li>
                        <li>• Hyggedrikker for vinterkulda</li>
                      </ul>
                    </div>
                    
                    <div className="border border-error-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🏠 Hjemmekos</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Duftlys og koselige ting</li>
                        <li>• Varme pledd og tøfler</li>
                        <li>• Bøker for juleferien</li>
                        <li>• Spa-produkter for avslapning</li>
                      </ul>
                    </div>
                    
                    <div className="border border-error-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">💝 Personlige gaver</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Julekort fra hver elev</li>
                        <li>• Klassens julehilsen i video</li>
                        <li>• Personaliserte julegaver</li>
                        <li>• Minnegaver fra skoleåret</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Just Thanks */}
              <div className={`${selectedOccasion === 'all' || selectedOccasion === 'thanks' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Flower2 className="w-10 h-10 text-primary-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Bare takk</h3>
                      <p className="text-secondary-600">Spontan takknemlighet som betyr mest</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="border border-primary-200 rounded-lg p-4">
                        <h4 className="font-semibold text-secondary-800 mb-3">🌻 Enkle men meningsfulle</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Et vakkert takke-kort</li>
                          <li>• Blomster fra hagen</li>
                          <li>• Hjemmelaget kake eller boller</li>
                          <li>• En kopp kaffe og en samtale</li>
                        </ul>
                      </div>
                      
                      <div className="border border-primary-200 rounded-lg p-4">
                        <h4 className="font-semibold text-secondary-800 mb-3">💌 Fra barnet selv</h4>
                        <ul className="text-sm text-secondary-600 space-y-1">
                          <li>• Håndskrevet brev fra eleven</li>
                          <li>• Tegning eller kunstprosjekt</li>
                          <li>• Video-hilsen fra barnet</li>
                          <li>• Fortelling om hva læreren betyr</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                      <h4 className="font-semibold text-primary-800 mb-3">💕 Når bør du gi en takk-gave?</h4>
                      <div className="text-sm text-primary-700 space-y-2">
                        <p><strong>Perfekte anledninger:</strong></p>
                        <ul className="space-y-1 ml-4">
                          <li>• Etter læreren har gått ekstra langt for barnet ditt</li>
                          <li>• Når barnet har hatt en vanskelig periode</li>
                          <li>• Etter en spesielt fin aktivitet eller tur</li>
                          <li>• Når du bare føler takknemlighet</li>
                        </ul>
                        <p className="mt-3 font-medium">Husk: Det er ordene som betyr mest, ikke verdien av gaven!</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Retirement */}
              <div className={`${selectedOccasion === 'all' || selectedOccasion === 'retirement' ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-lg shadow-sm p-8 border border-secondary-100">
                  <div className="flex items-center mb-6">
                    <Clock className="w-10 h-10 text-purple-500 mr-4" />
                    <div>
                      <h3 className="text-2xl font-bold text-secondary-800">Pensjonering</h3>
                      <p className="text-secondary-600">Hedre en lang og meningsfull karriére</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🏆 Ærespris og minner</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Minnebok med bidrag fra alle elever</li>
                        <li>• Foto-kollasje fra årene på skolen</li>
                        <li>• Personalisert plakett eller diplom</li>
                        <li>• Samlealbum med takk fra familier</li>
                      </ul>
                    </div>
                    
                    <div className="border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-secondary-800 mb-3">🌅 Nytt kapittel</h4>
                      <ul className="text-sm text-secondary-600 space-y-1">
                        <li>• Opplevelser og reisegaver</li>
                        <li>• Hobby-utstyr for nye interesser</li>
                        <li>• Bøker og kurs for pensjonisttilværelsen</li>
                        <li>• Hage-utstyr eller innendørs planter</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 mb-2">👑 En verdig avskjed</h4>
                    <p className="text-purple-700 text-sm">
                      Pensjonering av en lærer er slutten på en æra. Denne gaven bør hedre ikke bare deres 
                      år på din skole, men hele deres karrière som pedagog. Vurder å organisere en større 
                      feiring der tidligere elever og kolleger kan bidra med minner og takk.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Considerations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Praktiske hensyn når du gir gaver til lærere
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <Users className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Koordinering med andre</h3>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Snakk med andre foreldre først</li>
                  <li>• Vurder felles gave fremfor mange enkeltstående</li>
                  <li>• Unngå at læreren får 20 like krus</li>
                  <li>• Del kostnad på dyre gaver</li>
                  <li>• Lag en plan for større anledninger</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <Coffee className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Budsjett og rettferdighet</h3>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Hold budsjettet rimelig (50-200 kr per familie)</li>
                  <li>• Ikke press familier til å bidra mer enn de kan</li>
                  <li>• Tenk på at læreren har mange elever</li>
                  <li>• Verdien ligger i tanken, ikke prisen</li>
                  <li>• Vurder hjemmelagde alternativer</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <Home className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Profesjonelle grenser</h3>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Hold gavene passende og respektfulle</li>
                  <li>• Unngå altfor personlige ting</li>
                  <li>• Respekter lærerens tid og privatliv</li>
                  <li>• Gi gaven på en naturlig måte</li>
                  <li>• Ikke forvent spesiell behandling tilbake</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-error-50 border border-error-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-error-800 mb-4">❌ Unngå disse gavene</h3>
                <ul className="space-y-2 text-error-700 text-sm">
                  <li>• Altfor dyre gaver som kan virke upassende</li>
                  <li>• Personlige klær (størrelse/stil er vanskelig)</li>
                  <li>• Alkohol (ikke alle drikker, kan være problematisk)</li>
                  <li>• Parfyme eller sterkt duftende produkter</li>
                  <li>• Religiøse gjenstander (med mindre du vet deres ståsted)</li>
                  <li>• Ting som krever mye vedlikehold</li>
                </ul>
              </div>

              <div className="bg-success-50 border border-success-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-success-800 mb-4">✅ Sikre og populære valg</h3>
                <ul className="space-y-2 text-success-700 text-sm">
                  <li>• Gavekort til bokhandel eller kafé</li>
                  <li>• Kvalitets kaffe, te eller sjokolade</li>
                  <li>• Klasseromsrekvisita og materiell</li>
                  <li>• Bøker og magasiner</li>
                  <li>• Planter eller blomster</li>
                  <li>• Personlige takkebrev og kort</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIY and Budget Ideas */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Kreative og budsjettsmarte ideer
            </h2>

            <div className="space-y-8">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <div className="flex items-center mb-4">
                  <Heart className="w-8 h-8 text-primary-500 mr-3" />
                  <h3 className="text-xl font-semibold text-secondary-800">Hjemmelagde gaver som berører</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-3">Fra kjøkkenet:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Hjemmelaget konfekt eller cookies</li>
                      <li>• Krydderblandinger i pene glass</li>
                      <li>• Syltetøy eller chutneys</li>
                      <li>• Brød eller kaker</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-3">Håndverk og kreativitet:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Strikket skjerf eller votter</li>
                      <li>• Selvlagde duftlys</li>
                      <li>• Broderte håndklær</li>
                      <li>• Plantepotter med dekorasjon</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <div className="flex items-center mb-4">
                  <BookOpen className="w-8 h-8 text-accent-500 mr-3" />
                  <h3 className="text-xl font-semibold text-secondary-800">Elevenes bidrag</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-3">Kreative prosjekter:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Klassens egen bok med historier</li>
                      <li>• Kunstprosjekt der alle bidrar</li>
                      <li>• Video med hilsener fra hver elev</li>
                      <li>• Klassens egen sang eller dikt</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-3">Minner og takknemlighet:</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Scrapbook med årets høydepunkter</li>
                      <li>• Takketegninger fra hver elev</li>
                      <li>• Foto-kolasje av klassens opplevelser</li>
                      <li>• Brev som beskriver hva læreren betyr</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
                <div className="flex items-start space-x-4">
                  <Lightbulb className="w-6 h-6 text-warning-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-warning-800 mb-2">Den beste gaven koster ingenting</h3>
                    <p className="text-warning-700">
                      Ofte er den mest verdifulle gaven du kan gi en lærer en oppriktig takk - enten 
                      muntlig eller skriftlig - som beskriver konkret hvordan de har påvirket barnet ditt positivt. 
                      Disse ordene huskes og verdsettes langt utover alle fysiske gaver.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-success-500 to-accent-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å vise takknemlighet til en lærer?
            </h2>
            <p className="text-xl text-success-100 mb-8">
              Bruk vår gavefinner for å finne den perfekte måten å si takk på
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-success-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('recipient')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-success-600 transition-colors font-semibold"
              >
                Se andre mottakere
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForTeachersPage;